import React, { useState, useEffect, useRef } from "react";

function SearchBar({ onAddTrack, onPlayPreview }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const searchTimeout = useRef(null);
  
  // Debounce search input
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    
    clearTimeout(searchTimeout.current);
    
    searchTimeout.current = setTimeout(() => {
      performSearch(query);
    }, 500);
    
    return () => clearTimeout(searchTimeout.current);
  }, [query]);
  
  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    setError(null);
    
    try {
      // Check if MusicKit is available
      if (!window.MusicKit) {
        throw new Error("Apple MusicKit is not available. Please check your configuration.");
      }
      
      const music = window.MusicKit.getInstance();
      
      // Search for tracks
      const res = await music.api.search(searchQuery, { 
        types: ["songs"],
        limit: 20
      });
      
      setResults(res.songs?.data || []);
    } catch (err) {
      console.error("Search error:", err);
      setError(err.message || "Failed to search for music. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleAddTrack = (track) => {
    // Animation for add button
    const btn = document.getElementById(`add-btn-${track.id}`);
    if (btn) {
      // Update button style and text to show "Added" permanently
      btn.classList.add("added");
      btn.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Added
      `;
      btn.disabled = true; // Disable the button to prevent multiple adds
    }
    
    onAddTrack(track);
  };
  
  const handlePlayPreview = (track, event) => {
    // Only trigger preview if not clicking the Add button
    if (!event.target.closest('.btn-primary') && onPlayPreview) {
      onPlayPreview(track);
    }
  };

  // Generate loading skeleton rows
  const renderLoadingRows = () => {
    return Array(5).fill().map((_, i) => (
      <div className="song-row loading" key={`loading-${i}`}>
        <div className="song-artwork-container loading-circle"></div>
        <div className="song-info-container">
          <div className="loading-line" style={{ width: '70%' }}></div>
          <div className="loading-line" style={{ width: '40%' }}></div>
        </div>
        <div className="song-action-container">
          <div className="loading-button"></div>
        </div>
      </div>
    ));
  };

  return (
    <div className="search-container">
      <div className="search-input-wrapper">
        <span className="search-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
        
       <input
  className="search-input"
  placeholder="Search for songs on Apple Music..."
  value={query}
  onChange={(e) => setQuery(e.target.value)}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.target.blur(); // This dismisses the keyboard by removing focus
      performSearch(query);
    }
  }}
/>
      </div>
      
      {error && (
        <div style={{ 
          padding: '1rem', 
          backgroundColor: '#ffe8e8', 
          borderRadius: 'var(--radius-md)', 
          marginBottom: '1rem',
          color: '#d32f2f'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}
      
      <div className="results-container table-view">
        {isSearching ? (
          <div className="loading-table">
            {renderLoadingRows()}
          </div>
        ) : results.length > 0 ? (
          <div className="song-table">
            {results.map((track) => (
              <div 
                className="song-row slide-up clickable-row" 
                key={track.id}
                onClick={(e) => handlePlayPreview(track, e)}
              >
                <div className="song-artwork-container">
                  <img 
                    className="song-artwork-circle" 
                    src={track.attributes.artwork.url.replace("{w}x{h}", "120x120")} 
                    alt={`${track.attributes.name} album artwork`}
                    loading="lazy"
                  />
                </div>
                
                <div className="song-info-container">
                  <div className="song-title">{track.attributes.name}</div>
                  <div className="song-artist">{track.attributes.artistName}</div>
                </div>
                
                <div className="song-action-container">
                  <button 
                    id={`add-btn-${track.id}`}
                    className="btn btn-primary" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddTrack(track);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Add
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : query.length > 0 && !isSearching ? (
          <div className="empty-state">
            No results found for "{query}"
          </div>
        ) : query.length === 0 ? (
          <div className="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.5, margin: '0 auto 1rem', display: 'block' }}>
              <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" 
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Search for your favorite songs
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default SearchBar;